"use client"

import { useEffect, useRef, useState } from "react"
import { ArrowRight, Check, Send, X } from "lucide-react"
import { handleSubscribe } from "@/lib/subscribe"
import { handleFormSubmit } from "@/lib/submitForm"
import TurnstileGate from "@/components/TurnstileGate"

/**
 * Shared email-capture component. Used by:
 *  - Newsletter ("inline") — site-wide section with the JOIN THE SQUAD hero.
 *  - SubscribeForm  ("compact") — Hub sidebar subscribe box.
 *  - RsvpModal      ("rsvp-modal") — Hub event RSVP modal (name + email + guests).
 *
 * Variants are gated by a discriminated union so missing RSVP props are a
 * compile-time error.
 *
 * Submission routing:
 *  - `inline`  → posts to the `newsletter` workflow (via NEXT_PUBLIC_FORM_WEBHOOK_URL_NEWSLETTER).
 *  - `compact` → posts to the `hub-subscribe` workflow (via NEXT_PUBLIC_FORM_WEBHOOK_URL_HUB_SUBSCRIBE).
 *  - `rsvp-modal` stays client-only — it does NOT post to a workflow (it's a
 *    UI confirmation, not a real RSVP pipeline; we surface those elsewhere).
 */

type InlineProps = {
  variant: "inline"
}

type CompactProps = {
  variant: "compact"
}

type RsvpModalProps = {
  variant: "rsvp-modal"
  eventTitle: string
  open: boolean
  onClose: () => void
}

type EmailCaptureProps = InlineProps | CompactProps | RsvpModalProps

// Field treatment shared with the rest of the site: soft gray border → solid brand
// violet on focus (3px weight). From CLAUDE.md "form-input-border-style" rule.
const fieldClass =
  "w-full px-4 py-3 border-[3px] border-black/25 bg-white text-black font-sans text-base focus:outline-none focus:border-[#5A3290] transition-colors"

// Inner-tier CTA (CLAUDE.md two-tier system) — ~⅔ size, same brand DNA.
const submitCta =
  "inline-flex items-center justify-center gap-2 w-full bg-black text-white px-6 py-3 text-sm font-antonio font-bold uppercase tracking-widest rounded-none shadow-[4px_4px_0px_0px_#5A3290] transition-all hover:bg-[#5A3290] hover:shadow-[4px_4px_0px_0px_white] hover:-translate-y-0.5"

export default function EmailCapture(props: EmailCaptureProps) {
  if (props.variant === "inline") return <InlineNewsletter />
  if (props.variant === "compact") return <CompactSubscribe />
  return (
    <RsvpForm
      eventTitle={props.eventTitle}
      open={props.open}
      onClose={props.onClose}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* inline — Newsletter (full-width hero strip on /, /contact, /programs, etc) */
/* -------------------------------------------------------------------------- */

function InlineNewsletter() {
  const [email, setEmail] = useState("")

  return (
    <form
      onSubmit={handleFormSubmit("newsletter", {
        onSubmit: (data) => {
          handleSubscribe(data.email ?? "")
          setEmail("")
        },
        redirect: false,
      })}
      className="flex flex-col gap-3 max-w-md"
    >
      <div className="flex flex-col gap-3">
        <input
          type="email"
          name="email"
          required
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-14 px-6 bg-white text-black placeholder:text-black/50 font-antonio font-bold rounded-none border-0 outline-none focus:ring-2 focus:ring-[#7B45C1]"
        />
        <TurnstileGate>
          {({ verified }) => (
            <button
              type="submit"
              disabled={!verified}
              className="h-14 px-8 bg-[#7B45C1] hover:bg-[#5A3290] text-white font-antonio font-bold uppercase tracking-widest rounded-none shadow-[6px_6px_0px_0px_#1A0A2E] transition-all hover:-translate-y-1 flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-[#7B45C1]"
            >
              <Send className="w-4 h-4" />
              Subscribe
            </button>
          )}
        </TurnstileGate>
      </div>
    </form>
  )
}

/* ---------------------------------------------------------------------------- */
/* compact — Hub sidebar subscribe box (replaces the old SubscribeForm.tsx)     */
/* ---------------------------------------------------------------------------- */

function CompactSubscribe() {
  const [email, setEmail] = useState("")

  return (
    <form
      onSubmit={handleFormSubmit("hub-subscribe", {
        onSubmit: (data) => {
          handleSubscribe(data.email ?? "")
          setEmail("")
        },
        redirect: false,
      })}
      className="flex flex-col gap-3"
    >
      <div className="space-y-2">
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          aria-label="Your email address"
          className="w-full px-4 py-3 text-sm font-sans text-black border-2 border-violet/40 focus:border-violet focus:outline-none rounded-none bg-white"
        />
        <TurnstileGate>
          {({ verified }) => (
            <button
              type="submit"
              disabled={!verified}
              className="w-full bg-violet text-white px-5 py-3 text-sm font-antonio font-bold rounded-none uppercase tracking-wider shadow-[4px_4px_0px_0px_#341A45] transition-all hover:bg-[#341A45] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-violet"
            >
              SUBSCRIBE
            </button>
          )}
        </TurnstileGate>
      </div>
    </form>
  )
}

/* ---------------------------------------------------------------------------- */
/* rsvp-modal — Hub event RSVP modal (replaces the old RsvpModal.tsx)           */
/* ---------------------------------------------------------------------------- */

function RsvpForm({
  eventTitle,
  open,
  onClose,
}: {
  eventTitle: string
  open: boolean
  onClose: () => void
}) {
  const [submitted, setSubmitted] = useState(false)
  // Keep the latest onClose without forcing the keydown listener to resubscribe.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current()
    }
    document.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [open])

  // Reset to the form view whenever a fresh modal opens.
  useEffect(() => {
    if (open) setSubmitted(false)
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`RSVP for ${eventTitle}`}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/70 backdrop-blur-sm"
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg">
        {submitted ? (
          // Confirmation state — mirrors the thank-you page (black + violet, check badge).
          <div className="relative overflow-hidden bg-black px-6 py-12 text-center shadow-[8px_8px_0px_0px_#5A3290] sm:px-10">
            <div
              className="absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage:
                  "linear-gradient(#ffffff 2px, transparent 2px), linear-gradient(90deg, #ffffff 2px, transparent 2px)",
                backgroundSize: "40px 40px",
              }}
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center text-white/70 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative z-10 flex flex-col items-center">
              <div className="mb-6 flex h-16 w-16 items-center justify-center border-[3px] border-[#5A3290] bg-[#5A3290] shadow-[6px_6px_0px_0px_#000]">
                <Check className="h-8 w-8 text-white" strokeWidth={3} />
              </div>

              <span className="inline-block -rotate-1 bg-[#5A3290] px-4 py-1.5 font-antonio text-xs font-bold uppercase tracking-widest text-white shadow-[4px_4px_0px_0px_#000]">
                RSVP Received
              </span>

              <h3 className="mt-5 font-antonio text-4xl font-bold uppercase leading-[0.95] text-white sm:text-5xl">
                THANK <span className="text-[#5A3290]">YOU!</span>
              </h3>

              <p className="mt-5 max-w-sm text-base leading-relaxed text-white/80">
                You&apos;re on the list for{" "}
                <span className="text-[#5A3290]">{eventTitle}</span>. We&apos;ll
                send a confirmation with all the details shortly.
              </p>

              <button type="button" onClick={onClose} className={submitCta + " mt-8 !w-auto"}>
                DONE <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          // RSVP form.
          <div className="relative bg-white shadow-[8px_8px_0px_0px_#5A3290]">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center text-black/40 transition-colors hover:text-black"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="border-b-[3px] border-black bg-[#5A3290] px-6 py-5 sm:px-8">
              <p className="font-antonio text-xs font-bold uppercase tracking-widest text-white/80">
                RSVP
              </p>
              <h3 className="mt-1 font-antonio text-2xl font-bold uppercase tracking-wide text-white">
                {eventTitle}
              </h3>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                setSubmitted(true)
              }}
              className="space-y-5 px-6 py-7 sm:px-8"
              noValidate
            >
              <div>
                <label
                  htmlFor="rsvp-name"
                  className="mb-2 block font-antonio text-sm font-bold uppercase tracking-wider text-black"
                >
                  Name *
                </label>
                <input
                  id="rsvp-name"
                  name="name"
                  type="text"
                  required
                  className={fieldClass}
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label
                  htmlFor="rsvp-email"
                  className="mb-2 block font-antonio text-sm font-bold uppercase tracking-wider text-black"
                >
                  Email *
                </label>
                <input
                  id="rsvp-email"
                  name="email"
                  type="email"
                  required
                  className={fieldClass}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="rsvp-guests"
                  className="mb-2 block font-antonio text-sm font-bold uppercase tracking-wider text-black"
                >
                  Number of Guests
                </label>
                <input
                  id="rsvp-guests"
                  name="guests"
                  type="number"
                  min={0}
                  defaultValue={1}
                  className={fieldClass}
                />
              </div>

              <button type="submit" className={submitCta}>
                CONFIRM RSVP <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}